const $ = jQuery.noConflict();

'use strict';
const Chat = {
	socket: null,
	activeChats: {},
	typingStatus: {},
	isConnected: false,
	firstLoad: true,
	lastMsgId: 0,

	/** Initializes the chat system */
	init() {
		this.setupWebSocket();
		this.bindUIEvents();
		this.createChatContainer();
		this.loadUnreadChats(); // ✅ Load unread messages on page load
	},

	/** Sets up WebSocket connection and handles reconnection */
	setupWebSocket() {
		this.socket = new WebSocket(window.webSocketData.serverUrl);

		this.socket.onopen = () => {
			console.log('Connected to WebSocket');
			this.isConnected = true;
			this.socket.send(JSON.stringify({type: 'register', username: window.webSocketData.currentUser}));
		};

		this.socket.onmessage = (event) => {
			const data = JSON.parse(event.data);
			console.log(event);
			if (data.type === 'message') {
				this.receiveMessage(data.sender, data.receiver, data.message, data.id);
			} else if (data.type === 'seen') {
				this.updateMessageStatus(data.sender);
			} else if (data.type === 'typing') {
				this.updateTypingIndicator(data.sender, data.isTyping);
			}
		};

		this.socket.onerror = (error) => {
			console.error('WebSocket error:', error);
		};

		this.socket.onclose = () => {
			console.log('Disconnected from WebSocket, attempting to reconnect...');
			this.isConnected = false;
			setTimeout(() => this.setupWebSocket(), 3000); // Reconnect after 3 seconds
		};
	},

	loadUnreadChats() {
		fetch(`/wp-json/office-pulse-api/v1/chat/unread?user=${window.webSocketData.currentUser}`)
			.then(response => response.json())
			.then(response => {
				if (response.success) {
					response.unread_users.forEach(user => {
						const chatBox = this.createMinimizedChat(user.username);
						this.incrementUnreadCounter(chatBox, user.unread_count);
					});
				}
			})
			.catch(error => console.error('Error loading unread chats:', error));
	},

	/** Binds UI events */
	bindUIEvents() {
		document.addEventListener('click', (event) => {
			const target = event.target;

			// Ensure target is a valid element before calling matches()
			if (!(target instanceof Element)) return;

			// ✅ Open chat when clicking on a user
			if (target.matches('.js-user-chat') || target.closest('.js-user-chat')) {
				const username = target.closest('.js-user-chat').dataset.userUsername;
				if (username && username !== window.webSocketData.currentUser) {
					this.openChat(username);
				}
			}

			// ✅ Send message when clicking send button
			if (target.matches('.chat-send-btn')) {
				this.sendMessage(target.closest('.chat-box'));
			}

			// ✅ Toggle chat minimization when clicking the header
			if (target.matches('.chat-header') || target.closest('.chat-header')) {
				const chatBox = target.closest('.chat-box');
				if (chatBox) {
					if (chatBox.classList.contains('minimized')) {
						const username = chatBox.dataset.username;
						this.openChat(username);
					} else {
						chatBox.classList.add('minimized');
					}
				}
			}

			// ✅ Close chat window
			if (target.matches('.chat-remove-btn')) {
				const chatBox = target.closest('.chat-box');
				Chat.closeChat(chatBox);
			}
		});

		document.addEventListener('keydown', (event) => {
			if (!(event.target instanceof Element)) return;

			if (event.target.matches('.chat-input')) {
				if (event.key === 'Enter' && !event.shiftKey) {
					event.preventDefault();
					this.sendMessage(event.target.closest('.chat-box'));
				} else {
					this.notifyTyping(event.target.closest('.chat-box'));
				}
			}
		});

		document.addEventListener('blur', (event) => {
			if (!(event.target instanceof Element)) return;

			if (event.target.matches('.chat-input')) {
				this.stopTyping(event.target.closest('.chat-box'));
			}
		});

	},

	closeChat(chatBox) {
		const username = chatBox.dataset.username;
		delete this.activeChats[username]; // ✅ Remove from active chats
		chatBox.remove();
	},

	/** Creates the main chat container */
	createChatContainer() {
		if (!document.querySelector('.chat-container')) {
			const container = document.createElement('div');
			container.classList.add('chat-container');
			document.body.appendChild(container);
		}
	},

	/** Opens a chat window */
	openChat(username) {
		if (this.activeChats[username]) {
			const chatBox = this.activeChats[username];
			const wasMinimized = chatBox.classList.contains('minimized');

			chatBox.classList.remove('minimized');

			setTimeout(() => {
				this.scrollToFirstUnread(chatBox);
			}, 300);

			this.attachScrollListener(chatBox, username);

			if (wasMinimized) {
				this.resetUnreadCounter(chatBox);
				this.markAsSeen(username);
			}

			return;
		}

		const chatBox = this.createMinimizedChat(username);
		chatBox.classList.remove('minimized');

		setTimeout(() => {
			this.scrollToFirstUnread(chatBox);
		}, 300);

		this.attachScrollListener(chatBox, username);
		this.resetUnreadCounter(chatBox);
	},

	attachScrollListener(chatBox, username) {
		const chatMessages = chatBox.querySelector('.chat-messages');

		chatMessages.addEventListener('scroll', () => {
			const unreadMessages = chatBox.querySelectorAll('.chat-message.unread');

			unreadMessages.forEach(msg => {
				if (this.isElementFullyInViewport(msg)) {
					msg.classList.remove('unread'); // ✅ Mark message as read
				}
			});

			// ✅ If no unread messages are left, reset the counter and update DB
			if (chatBox.querySelectorAll('.chat-message.unread').length === 0) {
				this.resetUnreadCounter(chatBox);
				this.markAsSeen(username);
			}
		});
	},

	isElementFullyInViewport(el) {
		const rect = el.getBoundingClientRect();
		return (
			rect.top >= 0 &&
			rect.bottom <= (window.innerHeight || document.documentElement.clientHeight)
		);
	},

	scrollToFirstUnread(chatBox) {
		const unreadMessages = chatBox.querySelectorAll('.chat-message.unread');

		if (unreadMessages.length > 0) {
			const firstUnreadMessage = unreadMessages[0];

			firstUnreadMessage.scrollIntoView({behavior: 'instant', block: 'center'});

			// ✅ Ensure that unread messages are marked as read after scrolling
			setTimeout(() => {
				unreadMessages.forEach(msg => msg.classList.remove('unread'));
				this.resetUnreadCounter(chatBox);
				this.markAsSeen(chatBox.dataset.username); // ✅ Store in database
			}, 500); // Wait before marking as read
		} else {
			console.log('[scrollToFirstUnread] No unread messages found.');
		}
	},

	createMinimizedChat(username) {
		const chatBox = document.createElement('div');
		chatBox.classList.add('chat-box', 'minimized'); // ✅ Chat starts minimized
		chatBox.dataset.username = username;
		chatBox.innerHTML = `
        <div class="chat-header">
            <span class="chat-header-username">${username}</span>
            <span class="js-chat-header-counter chat-header-counter hidden">0</span>
            <button class="chat-remove-btn">×</button>
        </div>
        <div class="chat-messages"></div>
        <div class="typing-indicator hidden">Typing...</div>
        <div class="chat-input-container">
            <input type="text" class="chat-input" placeholder="Type a message..." autocomplete="off">
            <button class="chat-send-btn">Send</button>
        </div>
    `;

		document.querySelector('.chat-container').appendChild(chatBox);
		this.activeChats[username] = chatBox;

		// ✅ Load chat history but do not mark messages as seen immediately
		this.loadChatHistory(username);

		return chatBox;
	},

	/** Loads chat history */
	loadChatHistory(username) {
		fetch(`/wp-json/office-pulse-api/v1/chat/get?user1=${window.webSocketData.currentUser}&user2=${username}`)
			.then(response => response.json())
			.then(response => {
				if (response.success) {
					const chatBox = this.getOrCreateChat(username);
					const chatMessages = chatBox.querySelector('.chat-messages');
					chatMessages.innerHTML = '';

					let firstUnreadMessage = null;

					response.messages.forEach(msg => {
						let status = '✓'; // Default single checkmark
						let color = 'white';
						let isUnread = msg.is_read === '0' ? 'unread' : '';
						if (msg.is_read) {
							status = '✓✓'; // Green check if read
							color = 'green';
						} else if (msg.sender_username !== window.webSocketData.currentUser) {
							status = '✓✓'; // Double check if delivered
							color = 'green';
							if (!firstUnreadMessage) firstUnreadMessage = msg.id; // ✅ Identify first unread message
						}
						this.lastMsgId = msg.id;
						this.displayMessage(msg.sender_username, username, msg.message, msg.id, status, isUnread, color);
					});

					// ✅ If there are unread messages, scroll to the first one
					if (firstUnreadMessage) {
						this.scrollToFirstUnread(chatBox);
					}

					// ✅ Show unread counter if there are unread messages
					if (response.unread_count > 0) {
						this.incrementUnreadCounter(chatBox, response.unread_count);
					}
				}
			})
			.catch(error => console.error('Error loading chat history:', error));
	},

	sendMessage(chatBox) {
		const message = chatBox.querySelector('.chat-input').value.trim();
		if (!message) return;

		const receiver = chatBox.dataset.username;
		chatBox.querySelector('.chat-input').value = '';

		const tempMessageId = `msg-${Date.now()}`;
		// this.displayMessage(window.webSocketData.currentUser, receiver, message, tempMessageId, '✓'); // Message sent

		const data = {sender: window.webSocketData.currentUser, receiver, message};

		fetch('/wp-json/office-pulse-api/v1/chat/send', {
			method: 'POST',
			headers: {'Content-Type': 'application/json'},
			body: JSON.stringify(data),
		})
			.then(response => response.json())
			.then(response => {
				if (response.success) {
					data.type = 'message';
					data.id = response.message_id;

					// ✅ Ensure the WebSocket is open before sending
					if (this.socket.readyState === WebSocket.OPEN) {
						this.socket.send(JSON.stringify(data));
					} else {
						console.error('WebSocket is not open. Message not sent.');
					}
				}
			})
			.catch(error => console.error('Error sending message:', error));

		this.stopTyping(chatBox);
	},

	/** Receives a message */
	receiveMessage(sender, receiver, message, messageId) {
		const chatWith = receiver === window.webSocketData.currentUser ? sender : receiver;
		let chatBox = document.querySelector(`.chat-box[data-username="${chatWith}"]`);

		// ✅ If chat does not exist, create a minimized chat
		if (!chatBox) {
			chatBox = this.createMinimizedChat(chatWith);
		}

		// ✅ Determine if message should be unread
		const isUnread = chatBox.classList.contains('minimized');
		console.log(messageId);
		if (!messageId) {
			messageId = parseInt(this.lastMsgId) + 1;
		}
		// ✅ Display message and pass `isUnread` flag
		this.displayMessage(sender, chatWith, message, messageId, '✓✓', isUnread);

		// ✅ If chat is minimized, increase unread counter
		if (isUnread) {
			this.incrementUnreadCounter(chatBox);
		}
	},

	incrementUnreadCounter(chatBox, count = 1) {
		const counter = chatBox.querySelector('.js-chat-header-counter');
		if (counter) {
			let currentCount = parseInt(counter.textContent) || 0;
			currentCount += count;
			counter.textContent = currentCount;
			counter.classList.remove('hidden'); // ✅ Ensure counter is visible
		}
	},

	resetUnreadCounter(chatBox) {
		const counter = chatBox.querySelector('.js-chat-header-counter');
		if (counter) {
			counter.textContent = '0';
			counter.classList.add('hidden'); // ✅ Hide counter when reset
		}
	},

	updateMessageStatus(sender) {
		console.log(`[updateMessageStatus] Marking messages sent to ${sender} as read.`);
		const lastMsgId = parseInt(this.lastMsgId) + 1;
		// ✅ Update all messages **sent by the current user** (User A) that were read by the receiver (User B)
		document.querySelectorAll(`.chat-message[data-id="${lastMsgId}"] .message-status`).forEach((el) => {
			el.textContent = '✓✓';
			el.style.color = 'green'; // ✅ Green checkmarks when read
		});
	},

	markAsSeen: (() => {
		let timeoutId = {};

		return function(sender) {
			const chatBox = document.querySelector(`.chat-box[data-username="${sender}"]`);

			// ✅ Only mark as seen if chat is open and not minimized
			if (!chatBox || chatBox.classList.contains('minimized')) {
				return;
			}

			// ✅ Prevent multiple rapid requests for the same sender
			if (timeoutId[sender]) {
				clearTimeout(timeoutId[sender]);
			}

			timeoutId[sender] = setTimeout(() => {
				console.log(`[markAsSeen] Sending request to mark messages from ${sender} as read.`);

				// ✅ Send WebSocket notification to the **sender** that messages have been seen
				this.socket.send(JSON.stringify({
					type: 'seen',
					sender: sender, // Current user (User B) who is reading
					receiver: window.webSocketData.currentUser // The sender (User A) who should see the update
				}));

				// ✅ Update database to mark messages as read
				fetch('/wp-json/office-pulse-api/v1/chat/read', {
					method: 'POST',
					headers: {'Content-Type': 'application/json'},
					body: JSON.stringify({sender, receiver: window.webSocketData.currentUser}),
				});

			}, 500); // ✅ Delay execution to prevent rapid consecutive calls
		};
	})(),

	/** Displays a message in chat */
	displayMessage(sender, chatWith, message, messageId, status, isUnread = false, color = 'white') {
		const chatBox = this.getOrCreateChat(chatWith);
		const chatContent = chatBox.querySelector('.chat-messages');

		const messageDiv = document.createElement('div');
		messageDiv.classList.add('chat-message', sender === window.webSocketData.currentUser ? 'sent' : 'received');

		// ✅ Ensure unread messages get the correct class
		if (isUnread && sender !== window.webSocketData.currentUser) {
			messageDiv.classList.add('unread');
		}

		messageDiv.dataset.id = messageId;
		messageDiv.setAttribute('data-username', sender);
		messageDiv.innerHTML = `
        <div class="chat-bubble">${message} <span class="message-status" style="color: ${color}">${status}</span></div>
    `;

		chatContent.appendChild(messageDiv);
		chatContent.scrollTop = chatContent.scrollHeight;
	},

	/** Updates typing indicator */
	updateTypingIndicator(sender, isTyping) {
		// const chatBox = this.getOrCreateChat(sender);
		const chatBox = document.querySelector(`.chat-box[data-username="${sender}"]`);
		if (chatBox) {
			chatBox.querySelector('.typing-indicator').classList.toggle('hidden', !isTyping);
		}
	},

	/** Notifies that the user is typing */
	notifyTyping(chatBox) {
		this.socket.send(JSON.stringify({
			type: 'typing',
			sender: window.webSocketData.currentUser,
			receiver: chatBox.dataset.username,
			isTyping: true
		}));
	},

	/** Stops the typing indicator */
	stopTyping(chatBox) {
		this.socket.send(JSON.stringify({
			type: 'typing',
			sender: window.webSocketData.currentUser,
			receiver: chatBox.dataset.username,
			isTyping: false
		}));
	},


	/** Retrieves or creates a chat window */
	getOrCreateChat(username) {
		if (!this.activeChats[username]) this.openChat(username);
		return this.activeChats[username];
	}
};

export default Chat;
